import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { UserNotify } from "src/app/support/notify-decorator";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { environment } from "src/environments/environment";
import {
  MedicalService,
  MedicalServiceCreate,
  MedicalServiceLight,
  MedicalServiceUpdate,
  MedicalServiceUpdateVariations
} from "../../models/medical-service";
import { values, round, pickBy, first } from "lodash-es";
import {
  omitFalsyProperties,
  omitUndefinedAndNullProperties
} from "src/app/support/object";

@Injectable()
export class MedicalServiceBackend {
  private readonly http = inject(HttpClient);

  list(
    request: PageRequest & {
      rateTableId: string;
      select?: string | undefined;
    },
    requestServiceCoefficient: number
  ): Observable<PaginatedResult<MedicalService>> {
    return this.http
      .get<PaginatedResult<MedicalService>>(
        `${environment.snoots.api.healthcare}/rate-tables/${request.rateTableId}/medical-services`,
        {
          params: {
            p: request.page,
            s: request.pageSize,
            ...request.filterValues,
            select: request.select
          }
        }
      )
      .pipe(
        map((result) => {
          return {
            ...result,
            docs: result.docs.map(
              (x) => <MedicalService>extendDoc(x, requestServiceCoefficient)
            )
          };
        })
      );
  }

  searchAllLightByRateTable({
    page,
    pageSize,
    rateTableId,
    name
  }: {
    page: number;
    pageSize: number;
    rateTableId: string;
    name: string | undefined;
  }): Observable<MedicalService[]> {
    return this.http
      .get<PaginatedResult<MedicalService>>(
        `${environment.snoots.api.healthcare}/rate-tables/${rateTableId}/medical-services`,
        {
          params: omitFalsyProperties({
            p: page,
            s: pageSize,
            light: true,
            name
          })
        }
      )
      .pipe(map((response) => response.docs));
  }

  search(
    request: PageRequest,
    name: string
  ): Observable<PaginatedResult<MedicalService>> {
    return this.http.get<PaginatedResult<MedicalService>>(
      `${environment.snoots.api.healthcare}/medical-services`,
      {
        params: pickBy({
          p: request.page,
          s: request.pageSize,
          name
        })
      }
    );
  }

  searchLight(
    request: PageRequest,
    name: string
  ): Observable<PaginatedResult<MedicalServiceLight>> {
    return this.http.get<PaginatedResult<MedicalServiceLight>>(
      `${environment.snoots.api.healthcare}/medical-services`,
      {
        params: omitUndefinedAndNullProperties({
          p: request.page,
          s: request.pageSize,
          light: true,
          name
        })
      }
    );
  }

  listForRateTable(
    request: PageRequest,
    requestServiceCoefficient: number,
    rateTableId: string
  ): Observable<PaginatedResult<MedicalService>> {
    return this.http
      .get<PaginatedResult<MedicalService>>(
        `${environment.snoots.api.healthcare}/rate-tables/${rateTableId}/medical-services`,
        {
          params: omitUndefinedAndNullProperties({
            p: request.page,
            s: request.pageSize,
            ...request.filterValues
          })
        }
      )
      .pipe(
        map((result) => {
          return {
            ...result,
            docs: result.docs.map(
              (x) =>
                <MedicalService>(
                  extendDoc(x, requestServiceCoefficient, rateTableId)
                )
            )
          };
        })
      );
  }

  listForRateTableLight(
    request: PageRequest,
    requestServiceCoefficient: number | undefined,
    rateTableId: string
  ): Observable<PaginatedResult<MedicalServiceLight>> {
    return this.http
      .get<PaginatedResult<MedicalService>>(
        `${environment.snoots.api.healthcare}/rate-tables/${rateTableId}/medical-services`,
        {
          params: omitUndefinedAndNullProperties({
            p: request.page,
            s: request.pageSize,
            light: true,
            ...request.filterValues
          })
        }
      )
      .pipe(
        map((result) => {
          return {
            ...result,
            docs: result.docs.map(
              (x) =>
                <MedicalService>(
                  extendDoc(x, requestServiceCoefficient, rateTableId)
                )
            )
          };
        })
      );
  }

  load(
    medicalServiceId: string,
    requestServiceCoefficient?: number,
    rateTableId?: string | undefined
  ): Observable<MedicalService> {
    return this.http
      .get<MedicalService>(
        `${environment.snoots.api.healthcare}/medical-services/${medicalServiceId}`
      )
      .pipe(
        map(
          (x) =>
            <MedicalService>extendDoc(x, requestServiceCoefficient, rateTableId)
        )
      );
  }

  loadLight(
    id: string,
    requestServiceCoefficient?: number,
    rateTableId?: string | undefined
  ): Observable<MedicalServiceLight> {
    return this.http
      .get<MedicalServiceLight>(
        `${environment.snoots.api.healthcare}/medical-services/${id}`,
        {
          params: {
            light: true
          }
        }
      )
      .pipe(map((x) => extendDoc(x, requestServiceCoefficient, rateTableId)));
  }

  loadById(id: string): Observable<MedicalService> {
    return this.http
      .get<MedicalService>(
        `${environment.snoots.api.healthcare}/medical-services/${id}`
      )
      .pipe(map((x) => x));
  }

  findByExternalId({
    rateTableId,
    externalId
  }: {
    rateTableId: string;
    externalId: string;
  }): Observable<MedicalServiceLight | undefined> {
    return this.http
      .get<PaginatedResult<MedicalServiceLight>>(
        `${environment.snoots.api.healthcare}/rate-tables/${rateTableId}/medical-services`,
        {
          params: {
            light: true,
            externalId
          }
        }
      )
      .pipe(
        map((response) => {
          return first(response.docs);
        })
      );
  }

  @UserNotify({
    successMessage: marker("Prestazione creata"),
    errorMessage: marker("Impossibile creare la prestazione")
  })
  create({
    rateTableId,
    request
  }: {
    rateTableId: string;
    request: MedicalServiceCreate;
  }): Observable<void> {
    return this.http.post<void>(
      `${environment.snoots.api.healthcare}/rate-tables/${rateTableId}/medical-services`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Prestazione aggiornata"),
    errorMessage: marker("Impossibile aggiornare la prestazione")
  })
  update(id: string, request: MedicalServiceUpdate): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.healthcare}/medical-services/${id}`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Prestazione disattivato"),
    errorMessage: marker("Impossibile disattivare la prestazione")
  })
  disabled(id: string, request: Partial<MedicalService>): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.healthcare}/medical-services/${id}`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Prestazione eliminata"),
    errorMessage: marker("Impossibile eliminare la prestazione")
  })
  delete(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.healthcare}/medical-services/${id}`
    );
  }

  @UserNotify({
    successMessage: marker("Variazione aggiornata"),
    errorMessage: marker("Impossibile aggiornare la variazione")
  })
  updateVariation(
    id: string,
    payload: MedicalServiceUpdateVariations
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.healthcare}/medical-services/${id}`,
      payload
    );
  }

  @UserNotify({
    successMessage: marker("Prestazioni esportate in Excel"),
    errorMessage: marker("Impossibile esportare le prestazioni"),
    progressIndeterminate: true,
    progressMessage: marker("Esportazione prestazioni in corso")
  })
  exportMassiveExcell(medicalServiceIds: string[]): Observable<void> {
    return this.http.post<void>(
      `${environment.snoots.api.healthcare}/medical-services/exports`,
      { medicalServiceIds: medicalServiceIds }
    );
  }

  @UserNotify({
    errorMessage: marker("Impossibile avviare l'esportazione")
  })
  exportMassive({
    rateTableId,
    medicalServiceIds
  }: {
    rateTableId: string;
    medicalServiceIds: string[];
  }): Observable<void> {
    return this.http.post<void>(
      `${environment.snoots.api.healthcare}/rate-tables/${rateTableId}/exports`,
      {
        medicalServiceIds
      }
    );
  }
}

function extendDoc(
  medicalService: MedicalService | MedicalServiceLight,
  requestServiceCoefficient?: number,
  rateTableId?: string
): MedicalService | MedicalServiceLight {
  let calculatedPrice = medicalService.unitPrice;
  let calculatedMaxPrice = medicalService.maxUnitPrice;

  let variationPrice: number | undefined = undefined;
  let isPriceModified = false;

  if (
    rateTableId &&
    (<MedicalService>medicalService).variations &&
    values((<MedicalService>medicalService).variations).length > 0
  ) {
    const variation = values((<MedicalService>medicalService).variations).find(
      (x) => x.rateTableId === rateTableId
    );
    if (variation && variation.enabled) {
      variationPrice = variation.price;
    }
  }

  if (variationPrice) {
    calculatedPrice = variationPrice;
    calculatedMaxPrice = variationPrice;
    isPriceModified = true;
  } else {
    if (requestServiceCoefficient) {
      calculatedPrice = round(
        medicalService.unitPrice * (1 + requestServiceCoefficient),
        2
      );
      calculatedMaxPrice = medicalService.maxUnitPrice
        ? round(
            medicalService.maxUnitPrice * (1 + requestServiceCoefficient),
            2
          )
        : 0;
    }
  }

  let totalPrice = medicalService.unitPrice * (1 + medicalService.vat);
  let totalMaxPrice = medicalService.maxUnitPrice
    ? medicalService.maxUnitPrice * (1 + medicalService.vat)
    : 0;

  if (variationPrice) {
    totalPrice = round(variationPrice * (1 + medicalService.vat), 2);
  } else {
    if (requestServiceCoefficient) {
      totalPrice = round(
        medicalService.unitPrice *
          (1 + medicalService.vat) *
          (1 + requestServiceCoefficient),
        2
      );
      totalMaxPrice = medicalService.maxUnitPrice
        ? round(
            medicalService.maxUnitPrice *
              (1 + medicalService.vat) *
              (1 + requestServiceCoefficient),
            2
          )
        : 0;
    }
  }

  return {
    ...medicalService,
    totalPrice,
    totalMaxPrice,
    calculatedPrice,
    calculatedMaxPrice,
    isPriceModified
  };
}
